/**
 * AddProfileModal — Create a new Player Profile shell.
 *
 * Shells are user-owned identities that aggregate stats from one or
 * more roster entries (player_profile records). The user provides a
 * display name + jersey + optional description, then chooses members
 * via either:
 *   - "Add from Favorites" — multi-select of currently-starred roster
 *     entries on the user's own teams.
 *   - "Add Player Profile Link Code" — paste an 8-char code from
 *     another coach to bundle in a player from outside the user's teams.
 *
 * Either selection method (or both) is valid; the shell is created on
 * submit and the caller navigates to the new profile page.
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const NAVY = '#0B1824';
const ORANGE = '#e8963a';
const MUTED = '#8FA3B1';
const BORDER = '#DDE5EC';

export default function AddProfileModal({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState('');
  const [jersey, setJersey] = useState('');
  const [description, setDescription] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [code, setCode] = useState('');
  const [favLoading, setFavLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch favorites whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    setName(''); setJersey(''); setDescription(''); setCode('');
    setSelectedIds(new Set());
    setFavLoading(true);
    api.get('/players/favorites')
      .then(r => setFavorites(r.data?.players || []))
      .catch(() => setFavorites([]))
      .finally(() => setFavLoading(false));
  }, [open]);

  const toggle = (pid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  const submit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) { toast.error('Profile name is required'); return; }
    const trimmedCode = code.trim().toUpperCase();
    if (trimmedCode && !/^[A-Z0-9]{8}$/.test(trimmedCode)) {
      toast.error('Code must be 8 characters (A–Z, 0–9).');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/profiles', {
        name: trimmedName,
        jersey_number: jersey.trim() || null,
        description: description.trim() || null,
        member_player_ids: Array.from(selectedIds),
        code: trimmedCode || null,
      });
      toast.success(`Profile "${r.data.name}" created`);
      onOpenChange(false);
      onCreated && onCreated(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create profile');
    } finally {
      setSubmitting(false);
    }
  };

  const memberCount = selectedIds.size + (code.trim().length === 8 ? 1 : 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-profile-modal">
        <DialogHeader>
          <DialogTitle
            className="text-lg font-bold uppercase tracking-tight"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Add Player Profile
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            Create a profile shell that combines stats from multiple
            rosters. Pick from your favorites or paste a player link code.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-[.12em] mb-1 block"
              style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>
              Name
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Jordan H."
              data-testid="add-profile-name"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <Label className="text-[11px] font-bold uppercase tracking-[.12em] mb-1 block"
                style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>
                Jersey #
              </Label>
              <Input
                value={jersey}
                onChange={(e) => setJersey(e.target.value)}
                placeholder="23"
                maxLength={3}
                data-testid="add-profile-jersey"
                inputMode="numeric"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] font-bold uppercase tracking-[.12em] mb-1 block"
                style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>
                Description (optional)
              </Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="School + AAU"
                data-testid="add-profile-description"
              />
            </div>
          </div>

          {/* Favorites multi-select */}
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-[.12em] mb-2 flex items-center gap-1.5"
              style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>
              Add from Favorites
            </Label>
            {favLoading ? (
              <p className="text-[12px]" style={{ color: MUTED }}>Loading…</p>
            ) : favorites.length === 0 ? (
              <p className="text-[12px] py-3 text-center rounded-lg"
                style={{ color: MUTED, background: '#F1F5F9' }}
                data-testid="add-profile-favorites-empty">
                No favorited players. Star a player on the Teams page first.
              </p>
            ) : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto" data-testid="add-profile-favorites-list">
                {favorites.map(p => {
                  const checked = selectedIds.has(p.player_id);
                  return (
                    <button
                      key={p.player_id}
                      type="button"
                      onClick={() => toggle(p.player_id)}
                      data-testid={`add-profile-fav-${p.player_id}`}
                      className="w-full rounded-xl px-3 py-2.5 flex items-center gap-3 transition-all hover:bg-slate-50 active:scale-[.99]"
                      style={{
                        background: checked ? 'rgba(232,150,58,.10)' : '#fff',
                        border: `1.5px solid ${checked ? ORANGE : BORDER}`,
                      }}
                    >
                      <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: ORANGE, color: NAVY, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 14 }}>
                        {p.jersey_number || '–'}
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-[14px] font-bold truncate leading-tight" style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif" }}>
                          {p.name}
                        </p>
                        {p.team_name && (
                          <p className="text-[11px] mt-0.5 truncate uppercase tracking-[.06em]" style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>
                            {p.team_name}
                          </p>
                        )}
                      </div>
                      <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                        style={{
                          background: checked ? ORANGE : 'transparent',
                          border: `1.5px solid ${checked ? ORANGE : BORDER}`,
                        }}
                      >
                        {checked && <Check className="w-3.5 h-3.5" style={{ color: '#fff' }} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Link-code input */}
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-[.12em] mb-2 flex items-center gap-1.5"
              style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>
              <Search className="w-3 h-3" /> Add Player Profile Link Code
            </Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
              placeholder="ABCD1234"
              maxLength={8}
              data-testid="add-profile-code"
              className="tracking-[0.32em] uppercase font-mono text-center text-base"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 h-11 rounded-full"
              data-testid="add-profile-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={submitting || !name.trim()}
              data-testid="add-profile-submit"
              className="flex-1 h-11 rounded-full font-bold uppercase"
              style={{ background: NAVY, color: '#fff' }}
            >
              {submitting ? 'Creating…' : memberCount > 0 ? `Create (${memberCount})` : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
